import { useEffect, useState, type ReactNode } from 'react';
import { Theme } from '@radix-ui/themes';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { isCrawler, isNonProductionDomain } from 'helpers/authentication';
import { useAppNavigation } from 'helpers/useAppNavigation';
import usePullToRefresh from 'helpers/usePullToRefresh';
import { useWindowSize } from 'helpers/useWindowSize';
import { isWebPSupported } from 'helpers/utils';
import { useAppSelector } from 'store/store';
import DomainPasswordModal from './DomainPasswordModal';
import Footer from './Footer';
import NavBar from './NavBar';
import PushNotificationPopup from './PushNotificationPopup';
import { SmartAppDialog } from './SmartAppDialog';

export const Page: React.FC<{
  title: string;
  description?: string;
  keywords?: string;
  className?: string;
  children?: ReactNode;
  showNewPostButton?: boolean;
  showAds?: boolean;
  jsonLd?: string;
}> = ({ title, description, keywords, className, children, jsonLd, showNewPostButton = false }) => {
  const [domainPasswordModalOpen, setDomainPasswordModalOpen] = useState(false);
  const [domainPasswordCorrect, setDomainPasswordCorrect] = useState(
    () => sessionStorage.getItem('domainPasswordEntered') === 'true',
  );

  const { navigateBack } = useAppNavigation();
  const size = useWindowSize();
  const isDesktop = size.width > 1024;
  const isInteractingWithCarousel = useAppSelector((state) => state.ui.isInteractingWithCarousel);
  let isResolved = false;
  isWebPSupported().then(() => {
    isResolved = true;
  });
  const objWebp: { webp: boolean } = { webp: isResolved ? isWebPSupported() : false };
  const pageDescription = description
    ? `${description} - Cerca tutti i match, giocatori, le statistiche, i circoli e tornei di TENNIS - con il calcolo classifica sulle partite di Federtennis FITP`
    : 'Cerca tutti i match, giocatori, le statistiche, i circoli e tornei di TENNIS - con il calcolo classifica sulle partite di Federtennis FITP';
  const pageKeywords = keywords
    ? `${keywords} - risultati match di tennis partite - tornei FITP - giocatori italia federtennis - tennistalker - tennis stalker - calcolo classifica - classifiche FITP FIT - circoli - tornei - giocatori`
    : 'risultati match di tennis partite, tornei FITP, giocatori italia federtennis, tennistalker, tennis stalker';

  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const pageContentClassName = classNames(
    'mx-1 min-[375px]:mx-4 md:mx-0 md:pt-32 pb-48',
    { 'pt-20': !Capacitor.isNativePlatform() },
    { 'pt-36': Capacitor.isNativePlatform() },
    'transition-transform duration-300 transform',
  );
  useEffect(() => {
    // Check if domain requires password modal
    if (isNonProductionDomain() && !domainPasswordCorrect) {
      setDomainPasswordModalOpen(true);
    }
  }, [domainPasswordCorrect]);

  useEffect(() => {
    if (isInteractingWithCarousel) return; // 🚀 Skip swipe if interacting with carousel

    const handleTouchStart = (e: TouchEvent) => {
      setTouchStartX(e.touches[0].clientX);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX !== null) {
        const deltaX = e.changedTouches[0].clientX - touchStartX;
        // If swipe right by more than 150px, trigger back swipe
        if (deltaX > 150) {
          navigateBack();
        }
      }
      setTouchStartX(null);
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [touchStartX, navigateBack, isInteractingWithCarousel]);

  const { pullToRefreshProps, indicatorProps } = usePullToRefresh({
    enabled: Capacitor.isNativePlatform(),
    onRefresh: () => window.location.reload(),
    threshold: 400, // Optional: Adjust the pull threshold
  });

  const date = new Date().toISOString();

  const handlePasswordCorrect = () => {
    setDomainPasswordCorrect(true);
    setDomainPasswordModalOpen(false);
    sessionStorage.setItem('domainPasswordEntered', 'true');
  };

  return (
    <HelmetProvider>
      <Helmet>
        <meta charSet='utf-8' />
        <meta name='description' content={pageDescription} />
        <meta name='keywords' content={pageKeywords} />
        <title>{`${title} - TennisTalker`}</title>
        {window.location?.pathname && (
          <link rel='canonical' href={`https://www.tennistalker.it${window.location.pathname}`} />
        )}
        {window.location?.pathname && (
          <meta property='og:url' content={`https://www.tennistalker.it${window.location.pathname}`} />
        )}
        {window.location?.pathname && (
          <meta name='twitter:url' content={`https://www.tennistalker.it${window.location.pathname}`} />
        )}
        <meta property='og:title' content={`${title} - TennisTalker`} />
        <meta property='og:description' content={pageDescription} />
        <meta name='twitter:title' content={`${title} - TennisTalker`} />
        <meta name='twitter:description' content={pageDescription} />
        <meta property='og:updated_time' content={date} />
        <meta name='lastModified' content={date?.slice(0, 10)} />
        {jsonLd && <script type='application/ld+json'>{jsonLd}</script>}
      </Helmet>
      <Theme accentColor='lime'>
        <div {...pullToRefreshProps} className='flex'>
          <div {...indicatorProps} />
          {isDesktop && (
            <div className='flex flex-1 flex-col justify-around'>
              {/* <AdKaoraBanner slot={AdSlot.SPALLA_TOP} />
              <AdKaoraBanner slot={AdSlot.SPALLA_MIDDLE} />
              <AdKaoraBanner slot={AdSlot.SPALLA_BOTTOM} /> */}
            </div>
          )}
          <div
            className={classNames(
              'mx-auto flex w-full max-w-full flex-col lg:max-w-[calc(100%-10rem)] xl:max-w-[calc(100%-24rem)] 2xl:max-w-[calc(100%-48rem)]',
              className,
              objWebp,
            )}
          >
            <NavBar showNewPostButton={showNewPostButton} />
            <div className={pageContentClassName}>{children}</div>
            {!isCrawler() && !Capacitor.isNativePlatform() && <PushNotificationPopup />}
            <SmartAppDialog />
            <DomainPasswordModal open={domainPasswordModalOpen} onPasswordCorrect={handlePasswordCorrect} />
            <Footer />
          </div>
          {isDesktop && (
            <div className='flex flex-1 flex-col justify-around'>
              {/* <AdKaoraBanner slot={AdSlot.SPALLA_TOP} />
              <AdKaoraBanner slot={AdSlot.SPALLA_MIDDLE} />
              <AdKaoraBanner slot={AdSlot.SPALLA_BOTTOM} /> */}
            </div>
          )}
        </div>
      </Theme>
    </HelmetProvider>
  );
};

export default Page;
